import { useState, useEffect } from 'react';

// Diverse welcoming responses for sign up
const welcomingResponses = [
  "Hey there! Thanks for joining us. Ready to explore what we have to offer?",
  "Welcome aboard! We're excited to have you here. Curious to see what's in store?",
  "Great to meet you! Your learning adventure starts now. Want to discover more?",
  "Hello and welcome! We're thrilled you're here. Shall we dive into the details?"
];

// Explanation messages about TeachingOrb
const explanationMessages = [
  "TeachingOrb lets you generate AI-powered crash courses on any topic! Free users can browse and learn from community-generated courses, while Premium members unlock the ability to generate crash courses tailored to their specific interests and learning goals.",
  "With TeachingOrb, learning is at your fingertips! Explore a library of user-generated courses for free, or upgrade to Premium to generate a crash course on demand. Whether you're picking up a new skill or diving deep into a subject, we've got you covered.",
  "TeachingOrb is your gateway to instant learning! Free members can access a growing collection of community generated courses, while Premium learners can generate crash courses on any topic they choose. From quick overviews to comprehensive deep-dives, you're in control of your learning journey."
];

// Acknowledgment messages when user says no
const acknowledgmentMessages = [
  "No problem! Take your time exploring the plans below. When you're ready, just select the one that fits you best.",
  "That's perfectly fine! Feel free to browse the options at your own pace. Choose the plan that works for you whenever you're ready.",
  "Sounds good! The plans are all laid out. Pick whichever speaks to you when you're ready to continue."
];

export const getRandomWelcomingMessage = () => {
  const randomIndex = Math.floor(Math.random() * welcomingResponses.length);
  return welcomingResponses[randomIndex];
};

const getRandomExplanation = () => {
  const randomIndex = Math.floor(Math.random() * explanationMessages.length);
  return explanationMessages[randomIndex];
};

const getRandomAcknowledgment = () => {
  const randomIndex = Math.floor(Math.random() * acknowledgmentMessages.length);
  return acknowledgmentMessages[randomIndex];
};

export function useSignUpChat({
  stage,
  onStreamComplete,
  onStreamStart,
  isStreaming = true,
  onCharacterTyped
}: {
  stage: 'welcome' | 'explanation' | 'acknowledgment' | 'processing' | 'success';
  onStreamComplete?: () => void;
  onStreamStart?: () => void;
  isStreaming?: boolean;
  onCharacterTyped?: () => void;
}) {
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [currentWelcomeMessage, setCurrentWelcomeMessage] = useState('');
  const [currentExplanation, setCurrentExplanation] = useState('');
  const [currentAcknowledgment, setCurrentAcknowledgment] = useState('');

  // Generate message based on stage
  const getMessage = (currentStage: typeof stage) => {
    if (currentStage === 'welcome') {
      return currentWelcomeMessage || getRandomWelcomingMessage();
    } else if (currentStage === 'explanation') {
      return currentExplanation || getRandomExplanation();
    } else if (currentStage === 'acknowledgment') {
      return currentAcknowledgment || getRandomAcknowledgment();
    } else if (currentStage === 'processing') {
      return "Setting up your account...";
    } else if (currentStage === 'success') {
      return "All set! Let's get started.";
    }
    return '';
  };

  // Set messages when stage changes
  useEffect(() => {
    if (stage === 'welcome') {
      setCurrentWelcomeMessage(getRandomWelcomingMessage());
    } else if (stage === 'explanation') {
      setCurrentExplanation(getRandomExplanation());
    } else if (stage === 'acknowledgment') {
      setCurrentAcknowledgment(getRandomAcknowledgment());
    }
  }, [stage]);

  // Handle streaming for all stages
  useEffect(() => {
    const message = getMessage(stage);
    
    if (!message) {
      setDisplayedText('');
      setIsComplete(false);
      return;
    }

    if (!isStreaming) {
      setDisplayedText(message);
      setIsComplete(true);
      if (onStreamComplete) onStreamComplete();
      return;
    }

    // Notify that streaming is starting
    if (onStreamStart) onStreamStart();

    setDisplayedText('');
    setIsComplete(false);
    let currentIndex = 0;

    const intervalId = setInterval(() => {
      if (currentIndex < message.length) {
        setDisplayedText(message.slice(0, currentIndex + 1));
        currentIndex++;
        if (onCharacterTyped) onCharacterTyped();
      } else {
        clearInterval(intervalId);
        setIsComplete(true);
        if (onStreamComplete) onStreamComplete();
      }
    }, 30);

    return () => clearInterval(intervalId);
  }, [stage, isStreaming, currentWelcomeMessage, currentExplanation, currentAcknowledgment]);

  return {
    displayedText,
    isComplete,
    message: getMessage(stage)
  };
}
